import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import "../Schedule/Schedule.css";
import { getSoftCardTheme } from "./dispatch/useCardTheme";
import QuickCheckInModal from "../../components/patient/QuickCheckInModal";
import {
  createEncounter,
  listEncounters,
  updateEncounter,
} from "../../services/patientService";

function tsMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return value instanceof Date ? value.getTime() : 0;
}

function sortNewest(a, b) {
  return tsMillis(b.createdAt) - tsMillis(a.createdAt);
}

function clean(value) {
  return String(value || "").trim();
}

function stopEvent(e) {
  e.stopPropagation();
}

function isAtHQ(call) {
  return (
    call.patientAtHQ === true ||
    call.status === "arrivedHQ" ||
    !!call.hq?.bedNumber ||
    !!call.hq?.firstName
  );
}

function hasPatientLeftHQ(call) {
  if (call.hq?.discharged === true && call.hq?.boardClosed !== true) {
  return false;
}

  const ambulanceUnitId =
    call.hospitalTransport?.ambulanceUnitId || call.ambulanceUnitId || null;

  const ambulanceStatus = ambulanceUnitId
    ? String(call.unitStatuses?.[ambulanceUnitId] || "").toUpperCase()
    : "";

  const ambulanceTransporting =
    !!call.hospitalTransport?.transportingAt ||
    ["TRANSPORTING", "TO_DESTINATION", "AVAIL", "CLEAR"].includes(
      ambulanceStatus
    );

  const courtesyUnitId = call.courtesyTransportUnitId;

  const courtesyStatus = courtesyUnitId
    ? String(call.unitStatuses?.[courtesyUnitId] || "").toUpperCase()
    : "";

  const courtesyTransporting =
    ["TRANSPORTING", "TO_DESTINATION", "AVAIL", "CLEAR"].includes(
      courtesyStatus
    );

  return ambulanceTransporting || courtesyTransporting;
}

function hasEncounterSignature(encounter) {
  if (!encounter) return false;
  return Boolean(
    encounter?.signature?.patientSignature ||
      encounter?.signatureForm?.patientSignature ||
      encounter?.patientSignature ||
      encounter?.signatureDataUrl
  );
}

function buildEncounterSeed(call, checkIn = {}) {
  const hq = call.hq || {};
  const hasAssignedUnits =
    Array.isArray(call.assignedUnitIds) && call.assignedUnitIds.length > 0;

  const complaint =
    clean(checkIn.complaint) ||
    clean(hq.chiefComplaint) ||
    clean(call.nature) ||
    "";

  const incidentRequired = checkIn.incidentRequired === true;

  return {
    status: "open",
    arrivalDate: new Date().toISOString().slice(0, 10),
    arrivalTime: new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    chiefComplaint: complaint,
    bed: clean(hq.bedNumber) || "",
    seenAt: "HQ",
    source: hasAssignedUnits ? "Cart" : "Walk-up",
    notes: [clean(call.location), clean(call.nature)].filter(Boolean).join(" • "),
    reportCompletedBy: "",
    providerName: "",

    dispatchCallId: call.id,
    incidentRequired,
    incidentStatus: incidentRequired ? "pending" : "not-required",
    incidentPrompted: Boolean(checkIn.incidentPrompted),

    incident: {
      description: complaint,
    },
  };
}

function getCardTheme(call) {
  const hq = call.hq || {};
  const ambulanceRequested = !!call.hospitalTransport?.requested;
  const courtesyRequested = !!hq.courtesyRequested;
  const unitStatuses = Object.values(call.unitStatuses || {});
  const atHQ = isAtHQ(call);

  const transportingToHQ =
    !atHQ && (call.status === "transporting" || unitStatuses.includes("TO_HQ"));

  const activeEmergency =
    call.callType === "emergency" &&
    !atHQ &&
    !transportingToHQ &&
    ["dispatched", "onScene"].includes(call.status);

    if (hq.discharged === true) {
  return {
    borderColor: "#f97316",
    bgColor: "#fff7ed",
    titleColor: "#7c2d12",
    textColor: "#9a3412",
    statusColor: "#b91c1c",
    mutedColor: "#9a3412",
    dividerColor: "#fed7aa",
  };
}

  if (ambulanceRequested) return getSoftCardTheme("awaitingAmbulance");
  if (courtesyRequested) return getSoftCardTheme("awaitingCourtesy");
  if (atHQ) return getSoftCardTheme("atHQ");
  if (transportingToHQ) return getSoftCardTheme("transportingToHQ");
  if (activeEmergency) return getSoftCardTheme("activeEmergency");
  return getSoftCardTheme("default");
}

function getStatusLabel(call) {
  const hq = call.hq || {};
  const ambulanceRequested = !!call.hospitalTransport?.requested;
  const courtesyRequested = !!hq.courtesyRequested;
  const unitStatuses = Object.values(call.unitStatuses || {});
  const atHQ = isAtHQ(call);

  const courtesyUnitId = call.courtesyTransportUnitId;
  const courtesyStatus = courtesyUnitId
    ? String(call.unitStatuses?.[courtesyUnitId] || "").toUpperCase()
    : "";

  const transportingToHQ =
    !atHQ && (call.status === "transporting" || unitStatuses.includes("TO_HQ"));

  const activeEmergency =
    call.callType === "emergency" &&
    !atHQ &&
    !transportingToHQ &&
    ["dispatched", "onScene"].includes(call.status);

  if (ambulanceRequested) {
  if (call.hospitalTransport?.transportingAt) {
    return "PT Transported to Hosp";
  }

  const normalized = unitStatuses.map(s => String(s).toUpperCase());

  if (normalized.includes("ON_SCENE") || normalized.includes("ON-SCENE")) {
    return "Ambulance On Scene";
  }

  if (
    normalized.includes("DISP") ||
    normalized.includes("RESPONDING")
  ) {
    return "Ambulance Responding";
  }

  return "Awaiting Ambulance";
}

// ONLY fall back to discharged if NOT an ambulance case
if (call.hq?.discharged === true) return "PT Discharged";

  if (courtesyRequested) {
  if (
    call.status === "dispatched" ||
    ["DISP", "RESPONDING"].includes(courtesyStatus)
  ) {
    return "Courtesy Enroute";
  }

  if (
    call.status === "onScene" ||
    ["ON_SCENE", "ARRIVED"].includes(courtesyStatus)
  ) {
    return "Courtesy On Scene";
  }

  if (call.status === "transporting") {
    return "PT Discharged";
  }

  return "Courtesy Requested";
}

  if (atHQ) return "In Bed";
  if (transportingToHQ) return "Transporting to HQ";
  if (activeEmergency) return "Active Emergency";

  return "Active";
}

function formatShortNameFromParts(firstName = "", lastName = "") {
  const first = clean(firstName);
  const last = clean(lastName);
  if (!first && !last) return "";
  if (!last) return first;
  return `${first} ${last.charAt(0).toUpperCase()}`;
}

function formatShortName(fullName = "") {
  const parts = clean(fullName).split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}`;
}

function getPatientDisplayName(patientRecord, hq, call) {
  // If no name yet
  const hasName =
    patientRecord?.fullName ||
    patientRecord?.firstName ||
    patientRecord?.lastName ||
    hq?.firstName;

  if (!hasName) {
    if (!isAtHQ(call)) {
      return "Active Emergency";
    }
    return "Awaiting Check-In";
  }

  if (patientRecord?.fullName) return formatShortName(patientRecord.fullName);

  if (patientRecord?.firstName || patientRecord?.lastName) {
    return formatShortNameFromParts(
      patientRecord.firstName,
      patientRecord.lastName
    );
  }

  if (hq?.firstName) return clean(hq.firstName);

  return "";
}

const BED_NUMBERS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11"];

const isPhone = window.innerWidth <= 640;
const isTablet = window.innerWidth <= 1024;

const DISPOSITION_OPTIONS = [
  "Returned to Event",
  "Discharged Home",
  "Transported to Hospital via Private Vehicle",
  "Transported to Hospital via Ambulance",
];

const BED_TILE_HEIGHT = 62;
const VIP_TILE_HEIGHT = 76;

function getDisplayBedForCall(call, patientEncounterMap) {
  if (call?.hq?.discharged === true) return "";

  const encounter = getLinkedEncounter(call, patientEncounterMap);
  return clean(encounter?.bed) || clean(call?.hq?.bedNumber);
}

const smallInputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "9px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  fontSize: 14,
  boxSizing: "border-box",
  background: "#ffffff",
  color: "#0f172a",
  minHeight: 40,
};

const smallBtn = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 800,
  minHeight: 36,
  lineHeight: 1.1,
  whiteSpace: "nowrap",
};

const badgeBaseStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 22,
  height: 22,
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 900,
  border: "1px solid transparent",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.35)",
  zIndex: 1000,
  display: "grid",
  placeItems: "center",
  padding: 16,
};

const modalStyle = {
  width: "min(560px, 100%)",
  background: "#ffffff",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.24)",
  display: "grid",
  gap: 12,
};

function getBedTileStyle({ hasPatient, isDropActive, height = BED_TILE_HEIGHT, padding = 6 }) {
  return {
    height,
    border: `2px dashed ${isDropActive ? "#22c55e" : hasPatient ? "#86efac" : "#cbd5e1"}`,
    borderRadius: 14,
    background: isDropActive ? "#dcfce7" : hasPatient ? "#f0fdf4" : "#ffffff",
    padding,
    display: "grid",
    gridTemplateRows: hasPatient ? "1fr" : "auto 1fr",
    gap: 4,
    overflow: "hidden",
    transition: "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
    boxShadow: isDropActive ? "inset 0 0 0 1px rgba(34, 197, 94, 0.15)" : "none",
  };
}

function ActionModal({ title, subtitle, onClose, children, footer }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={stopEvent}>
        <div
          style={{
          display: "grid",
          gridTemplateColumns: isTablet
  ? "1fr"
  : "230px minmax(0, 1fr)",
          gap: 10,
          alignItems: "start",
        }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
              {title}
            </div>
            {subtitle ? (
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>

        {children}

        {footer ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TrackerPatientCard({
  call,
  patientRecord,
  encounter,
  chartState,
  chartBusyCallId,
  setChartModalCall,
  openLinkedChart,
  openCourtesyModal,
  openAmbulanceModal,
  assignBed,
  patientEncounterMap,
  badges,
  compact = false,
  onOpenBoardModal,
  draggable = false,
  onDragStart,
  onDragEnd,
}) {
  const hq = call.hq || {};
  const theme = getCardTheme(call);
  const statusLabel = getStatusLabel(call);

  const displayName = getPatientDisplayName(patientRecord, hq, call);
  const displayBed = getDisplayBedForCall(call, patientEncounterMap);
  const displayComplaint =
    clean(encounter?.chiefComplaint) ||
    clean(hq.chiefComplaint) ||
    clean(call.nature) ||
    "—";

  if (compact) {
    return (
      <div
        draggable={draggable}
        onDragStart={(e) => onDragStart?.(e, call)}
        onDragEnd={onDragEnd}
        style={{
          border: `2px solid ${theme.borderColor}`,
          background: theme.bgColor,
          borderRadius: 10,
          padding: 3,
          boxShadow: "0 1px 4px rgba(15, 23, 42, 0.05)",
          minWidth: 0,
          height: "100%",
          display: "grid",
          gridTemplateRows: "auto auto 1fr",
          gap: 1,
          overflow: "hidden",
          cursor: draggable ? "grab" : "default",
          alignContent: "start",
        }}
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: 9.5,
            color: theme.titleColor,
            lineHeight: 1.05,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={displayName || "Unnamed"}
        >
          {displayName || "Unnamed"}
        </div>

        <div
          style={{
            fontSize: 9,
            color: theme.textColor,
            fontWeight: 600,
            lineHeight: 1.05,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
          title={displayComplaint}
        >
          {displayComplaint}
        </div>

       {call.hq?.discharged === true && (
  <div
    style={{
      fontSize: 10,
      color: "#b91c1c",
      fontWeight: 900,
      lineHeight: 1.05,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
    title="PT Discharged"
  >
    PT Discharged
  </div>
)}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 4,
            borderTop: `1px solid ${theme.dividerColor}`,
            paddingTop: 2,
            marginTop: "auto",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              flexWrap: "nowrap",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {badges}
          </div>

          <button
            type="button"
            onClick={() => onOpenBoardModal(call)}
            style={{
              ...smallBtn,
              minHeight: 22,
              height: 22,
              padding: "0 8px",
              fontSize: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              borderColor: "#bbf7d0",
              background: "#f0fdf4",
              color: "#166534",
              flexShrink: 0,
            }}
          >
            Open
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, call)}
      onDragEnd={onDragEnd}
      style={{
        border: `2px solid ${theme.borderColor}`,
        background: theme.bgColor,
        borderRadius: 12,
        padding: 10,
        boxShadow: "0 3px 10px rgba(15, 23, 42, 0.08)",
        minWidth: 0,
        display: "grid",
        gap: 10,
        alignSelf: "start",
        cursor: draggable ? "grab" : "default",
      }}
    >
      <div style={{ display: "grid", gap: 4 }}>
        <div
          style={{
            fontWeight: 800,
            fontSize: 14,
            color: theme.titleColor,
            lineHeight: 1.15,
            wordBreak: "break-word",
          }}
        >
          {displayName || "Unnamed Patient"}
        </div>

        <div
          style={{
            fontSize: 12,
            color: theme.textColor,
            fontWeight: 600,
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {displayComplaint}
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: theme.statusColor,
            lineHeight: 1.1,
          }}
        >
          {statusLabel}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          borderTop: `1px solid ${theme.dividerColor}`,
          paddingTop: 8,
        }}
      >
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {badges}
        </div>

        {!chartState.isLinked ? (
          <button
            type="button"
            onClick={() => setChartModalCall(call)}
            disabled={chartBusyCallId === call.id}
            style={{
              ...smallBtn,
              borderColor: "#bfdbfe",
              background: "#eff6ff",
              color: "#1d4ed8",
            }}
          >
            {chartBusyCallId === call.id ? "Starting..." : "Start Chart"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => openLinkedChart(call)}
            style={{
              ...smallBtn,
              borderColor: "#bbf7d0",
              background: "#f0fdf4",
              color: "#166534",
            }}
          >
            Open Chart
          </button>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 8,
        }}
      >
        <select
          value={displayBed}
          onChange={(e) => assignBed(call, e.target.value)}
          style={{
            ...smallInputStyle,
            minHeight: 36,
            padding: "6px 8px",
            fontSize: 12,
          }}
        >
          <option value="">Assign Bed</option>
          {BED_NUMBERS.map((bed) => (
            <option key={bed} value={bed}>
              Bed {bed}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => openCourtesyModal(call)}
          style={{
            ...smallBtn,
            borderColor: "#fde68a",
            background: "#fef9c3",
            color: "#a16207",
          }}
        >
          Courtesy
        </button>

        <button
          type="button"
          onClick={() => openAmbulanceModal(call)}
          style={{
            ...smallBtn,
            borderColor: "#fca5a5",
            background: "#fee2e2",
            color: "#b91c1c",
            gridColumn: "1 / -1",
          }}
        >
          Ambulance
        </button>
      </div>

      {!chartState.isLinked && (
        <div
          style={{
            fontSize: 11,
            color: theme.mutedColor,
            borderTop: `1px solid ${theme.dividerColor}`,
            paddingTop: 8,
          }}
        >
          Original Call: {call.location || "—"} • {call.nature || "—"}
        </div>
      )}
    </div>
  );
}

function BedDropTile({
  label,
  bed,
  bedCalls,
  isVip = false,
  onOpenBoardModal,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDropActive,
  patientMap,
  renderChartBadges,
  getChartState,
}) {
  const primaryCall = bedCalls[0] || null;
  const extraCount = Math.max(0, bedCalls.length - 1);
  const hasPatient = Boolean(primaryCall);
  const height = isVip ? VIP_TILE_HEIGHT : BED_TILE_HEIGHT;

  const chartState = primaryCall ? getChartState(primaryCall) : null;
  const encounter = chartState?.currentEncounter || null;
  const patientRecord = primaryCall ? patientMap[primaryCall.patientId] || null : null;
  const theme = primaryCall ? getCardTheme(primaryCall) : null;
  const badges = primaryCall
    ? renderChartBadges(primaryCall, theme, chartState)
    : null;
  const patientName = primaryCall
  ? getPatientDisplayName(patientRecord, primaryCall.hq || {}, primaryCall)
  : "";
  const complaint = primaryCall
  ? clean(encounter?.chiefComplaint) ||
    clean(primaryCall.hq?.chiefComplaint) ||
    clean(primaryCall.nature) ||
    ""
  : "";
  const transportStatus = primaryCall ? getStatusLabel(primaryCall) : "";

const showTransportStatus =
  primaryCall &&
  [
    "Awaiting Ambulance",
    "Ambulance Responding",
    "Ambulance On Scene",
    "Courtesy Requested",
    "Courtesy Enroute",
    "Courtesy On Scene",
  ].includes(transportStatus);

return (
  <div
    onDragOver={(e) => onDragOver(e, bed)}
    onDragLeave={() => onDragLeave(bed)}
    onDrop={(e) => onDrop(e, bed)}
    style={getBedTileStyle({
      hasPatient,
      isDropActive,
      height,
      padding: hasPatient ? 0 : 4,
    })}
  >
    {!primaryCall ? (
      <>
        <div style={{ fontWeight: 900, color: "#334155" }}>{label}</div>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>Empty</div>
      </>
    ) : (
      <div
        draggable
        onDragStart={(e) => onDragStart?.(e, primaryCall)}
        onDragEnd={onDragEnd}
        style={{
        height: "100%",
border: `2px solid ${theme.borderColor}`,
background: theme.bgColor,
borderRadius: 12,
padding: "6px 8px",
display: "grid",
gridTemplateColumns: "1fr auto auto",
gridTemplateRows: "1fr 1fr",
alignItems: "center",
columnGap: 8,
rowGap: 0,
overflow: "hidden",
cursor: "grab",  
        }}
      >
         <div
  style={{
  gridColumn: 2,
  gridRow: 2,
  fontSize: 9,
  fontWeight: 900,
  color: "#334155",
  alignSelf: "center",
  justifySelf: "end",
  lineHeight: 1,
  opacity: 0.75,
  whiteSpace: "nowrap",
}}
>
  {label}
</div>    

            <div
        style={{
          gridColumn: 1,
          gridRow: 1,
          fontSize: 12,
          fontWeight: 900,
          color: theme.titleColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: 1.1,
        }}
      >
        {patientName}
      </div>

        <button
          type="button"
          onClick={() => onOpenBoardModal(primaryCall)}
          style={{
            ...smallBtn,
            minHeight: 28,
            height: 28,
            padding: "0 10px",
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            borderColor: "#bbf7d0",
            background: "#f0fdf4",
            color: "#166534",
            gridColumn: 2,
            gridRow: 1,
            alignSelf: "center",
            alignSelf: "end",
            marginTop: 2,
            justifySelf: "center",
          }}
        >
          Open
        </button>

       <div
  style={{
    gridColumn: 1,
    gridRow: 2,
    display: "grid",
    gap: 1,
    overflow: "hidden",
    minWidth: 0,
  }}
>
  <div
    style={{
      display: "flex",
      gap: 5,
      alignItems: "center",
      overflow: "hidden",
      minWidth: 0,
    }}
  >
    {badges}
    <span
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        color: theme.statusColor,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {complaint}
    </span>
  </div>

  {showTransportStatus && (
  <div
    style={{
      gridColumn: 2,
      gridRow: 1,
      fontSize: 10,
      fontWeight: 900,
      alignSelf: "center",
      justifySelf: "center",
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      gap: 4,

      color: transportStatus.includes("Ambulance")
        ? "#b91c1c"
        : "#a16207",
    }}
    title={transportStatus}
  >
    <span>
      {transportStatus.includes("Ambulance") ? "🚑" : "🚐"}
    </span>
    <span>{transportStatus}</span>
  </div>
)}
 </div>
    </div>
  )}
</div>
);
}
export default function HQ({ onTotalPatientsChange, onInventoryHandlerReady }) {
  const navigate = useNavigate();

  const [calls, setCalls] = useState([]);
  const [patients, setPatients] = useState([]);
  const [incidentReports, setIncidentReports] = useState([]);
  const [patientEncounterMap, setPatientEncounterMap] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const [chartModalCall, setChartModalCall] = useState(null);
  const [chartBusyCallId, setChartBusyCallId] = useState("");
  const [boardModalCall, setBoardModalCall] = useState(null);
  const [courtesyModal, setCourtesyModal] = useState(null);
  const [ambulanceModal, setAmbulanceModal] = useState(null);
  const [dispositionModal, setDispositionModal] = useState(null);
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [hqInventories, setHqInventories] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  

  const [draggingCallId, setDraggingCallId] = useState("");
  const [dropTarget, setDropTarget] = useState(null);

 const patientMap = useMemo(() => {
  const map = {};
  patients.forEach((patient) => {
    map[patient.id] = patient;
  });
  return map;
}, [patients]);

const manualHqCalls = useMemo(() => {
  return [];
}, []);

useEffect(() => {
 async function createDispatchCallsForManualPatients() {
  const existingPatientIds = new Set(
  calls.map((call) => String(call.patientId || "")).filter(Boolean)
);

  const manualPatients = patients.filter((patient) => {
    const hasActiveTrackerStatus =
      !patient.trackerStatus || patient.trackerStatus === "active";

    const hasHqLocation =
      patient.currentBed ||
      patient.bed ||
      patient.hqBed ||
      patient.assignedBed ||
      patient.waitingRoom === true ||
      patient.currentLocation === "Waiting Room" ||
      patient.location === "Waiting Room";

    const isClosed =
      patient.closed === true ||
      patient.discharged === true ||
      patient.trackerStatus === "closed";

    return (
      hasActiveTrackerStatus &&
      hasHqLocation &&
      !isClosed &&
      !existingPatientIds.has(String(patient.id))
    );
  });

  for (const patient of manualPatients) {
    try {
      const hqBedNumber = clean(
        patient.currentBed ||
          patient.bed ||
          patient.hqBed ||
          patient.assignedBed
      );

      const dispatchRef = await addDoc(collection(db, "dispatchCalls"), {
        callType: "emergency",
        source: "manualTracker",
        sourcePatientId: patient.id,

        patientId: patient.id,
        encounterId: patient.currentEncounterId || "",

        status: "arrivedHQ",
        patientAtHQ: true,

        location: "Medical HQ",
        nature: patient.chiefComplaint || "Walk-up",
        notes: "Manual walk-up patient added from PT Tracker",

        assignedUnitIds: [],
        unitStatuses: {},
        recommendedUnitIds: [],
        suggestedSupervisorIds: [],
        clearedUnitIds: [],

        createdAt: patient.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),

        hospitalTransport: {
          requested: false,
          acknowledged: false,
          destinationHospital: null,
          ambulanceUnitId: null,
          requestedAt: null,
          dispatchedAt: null,
          transportingAt: null,
          availableAt: null,
        },

        ambulanceUnitId: null,

        hq: {
          bedNumber: hqBedNumber || null,
          firstName: patient.firstName || patient.fullName || "",
          lastInitial: patient.lastName
            ? String(patient.lastName).charAt(0).toUpperCase()
            : null,
          chiefComplaint: patient.chiefComplaint || "",
          consentSigned: false,
          incidentReportCompleted: false,
          pcrCompleted: false,
          discharged: false,
          dischargeDisposition: null,
          courtesyRequested: false,
          courtesyAcknowledged: false,
          courtesyQueued: false,
          courtesyDestination: null,
          courtesyPickupLocation: null,
          courtesyPassengerCount: null,
        },
      });

      await updateDoc(doc(db, "patients", patient.id), {
        dispatchCallId: dispatchRef.id,
        trackerStatus: "active",
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to create manual tracker dispatch call:", err);
      setErrMsg(err?.message || "Could not create tracker call.");
    }
  }
}

  createDispatchCallsForManualPatients();
}, [patients]);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "dispatchCalls"),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(sortNewest);

        const hqCalls = all.filter((call) => {
          if (call.hq?.boardClosed === true) return false;

          if (
            (call.status === "completed" || call.status === "cancelled") &&
            call.hq?.discharged !== true
          ) {
            return false;
          }
          if (call.callType !== "emergency") return false;

       const atHQ = isAtHQ(call);

const normalizedStatus = String(call.status || "").trim();

const unitStatuses = Object.values(call.unitStatuses || {}).map((s) =>
  String(s || "").trim().toUpperCase()
);

const hasAssignedUnits =
  Array.isArray(call.assignedUnitIds) && call.assignedUnitIds.length > 0;

const activeEmergency =
  ["dispatched", "onScene", "transporting", "arrivedHQ"].includes(normalizedStatus) ||
  hasAssignedUnits ||
  unitStatuses.some((s) =>
    ["DISP", "RESPONDING", "ON_SCENE", "TO_HQ", "TRANSPORTING", "ARRIVED_HQ"].includes(s)
  );

const transportingToHQ =
  normalizedStatus === "transporting" ||
  unitStatuses.includes("TO_HQ") ||
  unitStatuses.includes("TRANSPORTING");

          const leavingHQ = hasPatientLeftHQ(call);

          const relatedToBoard =
            activeEmergency ||
            transportingToHQ ||
            (atHQ && !leavingHQ) ||
            (!!call.hospitalTransport?.requested && !leavingHQ) ||
            (!!call.hq?.courtesyRequested && !leavingHQ);

          return relatedToBoard;
        });

        setCalls(hqCalls);
      },
      (err) => {
        console.error("HQ subscribe failed:", err);
        setErrMsg(err?.message || "Could not load HQ patients.");
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "patients"),
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(sortNewest);
        setPatients(items);
      },
      (err) => {
        console.error("Patients subscribe failed:", err);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "incident_reports"),
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setIncidentReports(items);
      },
      (err) => {
        console.error("Incident reports subscribe failed:", err);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const patientIds = Array.from(
      new Set(calls.map((call) => clean(call.patientId)).filter(Boolean))
    );

    if (!patientIds.length) {
      setPatientEncounterMap({});
      return undefined;
    }

    const unsubs = patientIds.map((patientId) =>
      onSnapshot(
        collection(db, "patients", patientId, "encounters"),
        (snap) => {
          const encounters = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => tsMillis(b.createdAt) - tsMillis(a.createdAt));

          setPatientEncounterMap((prev) => ({
            ...prev,
            [patientId]: encounters,
          }));
        },
        (err) => {
          console.error(`Encounter subscribe failed for patient ${patientId}:`, err);
        }
      )
    );

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [calls]);

  const sortedCalls = useMemo(() => {
  return [...calls].sort((a, b) => {
      const aEncounter = getLinkedEncounter(a, patientEncounterMap);
      const bEncounter = getLinkedEncounter(b, patientEncounterMap);

      const aBedRaw = clean(aEncounter?.bed) || clean(a.hq?.bedNumber);
      const bBedRaw = clean(bEncounter?.bed) || clean(b.hq?.bedNumber);

      const aBed = Number.parseInt(String(aBedRaw).replace(/[^\d]/g, ""), 10);
      const bBed = Number.parseInt(String(bBedRaw).replace(/[^\d]/g, ""), 10);

      const aHasBed = Number.isFinite(aBed);
      const bHasBed = Number.isFinite(bBed);

      if (!aHasBed && !bHasBed) return 0;
      if (!aHasBed) return -1;
      if (!bHasBed) return 1;

      return aBed - bBed;
    });
 }, [calls, manualHqCalls, patientEncounterMap]);

  const unassignedCalls = useMemo(() => {
    return sortedCalls.filter(
      (call) => !getDisplayBedForCall(call, patientEncounterMap)
    );
  }, [sortedCalls, patientEncounterMap]);

  const callsByBed = useMemo(() => {
    const map = {};
    BED_NUMBERS.forEach((bed) => {
      map[bed] = sortedCalls.filter(
        (call) => getDisplayBedForCall(call, patientEncounterMap) === bed
      );
    });
    return map;
  }, [sortedCalls, patientEncounterMap]);

  function getChartState(call) {
    const patientId = clean(call.patientId);
    const encounterId = clean(call.encounterId);
    const patientEncounters = patientEncounterMap[patientId] || [];
    const currentEncounter = encounterId
      ? patientEncounters.find((enc) => enc.id === encounterId) || null
      : null;

    const currentSignature = hasEncounterSignature(currentEncounter);
    const anySignatureOnFile = patientEncounters.some((enc) =>
      hasEncounterSignature(enc)
    );

    const incidentFromReports = incidentReports.some(
      (report) =>
        clean(report.patientId) === patientId &&
        clean(report.encounterId) === encounterId
    );

    const incidentFromEncounterDocuments =
      Array.isArray(currentEncounter?.documents) &&
      currentEncounter.documents.some(
        (docItem) =>
          clean(docItem?.category) === "incident-report" ||
          clean(docItem?.type) === "incident-report"
      );

    const hasIncident = Boolean(
      encounterId && (incidentFromReports || incidentFromEncounterDocuments)
    );

    return {
      isLinked: Boolean(patientId && encounterId),
      currentEncounter,
      hasCurrentSignature: currentSignature,
      hasSignatureOnFile: anySignatureOnFile,
      hasIncident,
    };
  }

  async function openInventoryModal() {
  setInventoryModalOpen(true);
  setInventoryLoading(true);

  try {
    const [assignmentSnap, templateSnap] = await Promise.all([
      getDocs(collection(db, "inventoryKitAssignments")),
      getDocs(collection(db, "inventoryKitTemplates")),
    ]);

    const templates = templateSnap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    const assignments = assignmentSnap.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter((assignment) => {
        const slug = String(assignment.locationSlug || "").toLowerCase();
        const name = String(assignment.locationName || "").toLowerCase();

        return (
          assignment.active !== false &&
          (slug === "medicalhq" || slug === "hq" || name.includes("hq"))
        );
      });

    const assignedTemplates = assignments
      .map((assignment) => {
        const template = templates.find((t) => t.id === assignment.templateId);
        return {
          assignment,
          template,
        };
      })
      .filter((entry) => entry.template);

    setHqInventories(assignedTemplates);
  } catch (err) {
    console.error("Failed to load HQ inventories:", err);
    setErrMsg("Could not load HQ inventories.");
  } finally {
    setInventoryLoading(false);
  }
}

useEffect(() => {
  if (typeof onInventoryHandlerReady === "function") {
    onInventoryHandlerReady(() => openInventoryModal);
  }

  return () => {
    if (typeof onInventoryHandlerReady === "function") {
      onInventoryHandlerReady(null);
    }
  };
}, [onInventoryHandlerReady]);

  function renderChartBadges(call, theme, chartState) {
    const badges = [];

    if (chartState.hasCurrentSignature) {
      badges.push(
        <span
          key="sig-current"
          title="Signature obtained for current chart"
          style={{
            ...badgeBaseStyle,
            background: "#dcfce7",
            color: "#166534",
            borderColor: "#86efac",
          }}
        >
          ✍
        </span>
      );
    } else if (chartState.hasSignatureOnFile) {
      badges.push(
        <span
          key="sig-file"
          title="Signature on file"
          style={{
            ...badgeBaseStyle,
            background: "#ecfeff",
            color: "#155e75",
            borderColor: "#a5f3fc",
          }}
        >
          ✍
        </span>
      );
    }

   const incidentRequired =
  chartState.currentEncounter?.incidentRequired === true ||
  call.hq?.incidentRequired === true;

if (incidentRequired && !chartState.hasIncident) {
  badges.push(
    <span
      key="incident-required"
      title="Incident report required but not completed"
      style={{
        ...badgeBaseStyle,
        background: "#fef3c7",
        color: "#92400e",
        borderColor: "#f59e0b",
      }}
    >
      !
    </span>
  );
}

if (chartState.hasIncident) {
  badges.push(
    <span
      key="incident-complete"
      title="Incident report completed for this encounter"
      style={{
        ...badgeBaseStyle,
        background: "#dcfce7",
        color: "#166534",
        borderColor: "#86efac",
      }}
    >
      ✓
    </span>
  );
}

    if (!badges.length && !savingKey.startsWith(`${call.id}:`)) return null;

    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {badges}
        {savingKey.startsWith(`${call.id}:`) && (
          <span
            style={{
              color: theme.mutedColor,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Saving…
          </span>
        )}
      </div>
    );
  }

  async function updateCall(callId, patch, key = "") {
    setErrMsg("");
    setSavingKey(key);
    try {
      await updateDoc(doc(db, "dispatchCalls", callId), patch);
    } catch (err) {
      console.error("HQ update failed:", err);
      setErrMsg(err?.message || "Could not save changes.");
    } finally {
      setSavingKey("");
    }
  }

async function syncDispositionToEncounter(call, disposition) {
  const patientId = clean(call.patientId);
  const encounterId = clean(call.encounterId);
  if (!patientId || !encounterId) return;

  await updateDoc(
    doc(db, "patients", patientId, "encounters", encounterId),
    {
      "medical.disposition": disposition,
      updatedAt: serverTimestamp(),
    }
  );
}

  async function applyDisposition(call, disposition) {
  if (call.manualTrackerOnly) {
    const patientId = clean(call.patientId);
    const encounterId = clean(call.encounterId);

    await updateDoc(doc(db, "patients", patientId), {
      dischargeDisposition: disposition,
      updatedAt: serverTimestamp(),
    });

    if (patientId && encounterId) {
      await syncDispositionToEncounter(call, disposition);
    }

    setDispositionModal(null);
    return;
  }

  await updateCall(
    call.id,
    {
      "hq.dischargeDisposition": disposition,
    },
    `${call.id}:disposition`
  );

  await syncDispositionToEncounter(call, disposition);
  setDispositionModal(null);
}

async function closePatientTrackerRecord(patientId, extraPatch = {}) {
  if (!patientId) return;

  await updateDoc(doc(db, "patients", patientId), {
    trackerStatus: "closed",
    currentBed: "",
    bed: "",
    hqBed: "",
    assignedBed: "",
    waitingRoom: false,
    currentLocation: "",
    location: "",
    closed: true,
    discharged: true,
    updatedAt: serverTimestamp(),
    ...extraPatch,
  });
}

async function closeEncounterAndRemoveFromBoard(call) {
  const confirmed = window.confirm(
    "Close this encounter and remove this patient from the HQ board?"
  );

  if (!confirmed) return;

  const patientId = clean(call.patientId);
  const encounterId = clean(call.encounterId);

  setErrMsg("");
  setSavingKey(`${call.id}:close`);

  try {
    if (patientId && encounterId) {
      await updateEncounter(patientId, encounterId, {
        status: "closed",
        closedAt: serverTimestamp(),
      });
    }

    if (patientId) {
      await closePatientTrackerRecord(patientId);
    }

    await updateCall(
      call.id,
      {
        "hq.boardClosed": true,
        "hq.closedAt": serverTimestamp(),
      },
      `${call.id}:close`
    );

    setBoardModalCall(null);
  } catch (err) {
    console.error("Close encounter failed:", err);
    setErrMsg(err?.message || "Could not close encounter.");
  } finally {
    setSavingKey("");
  }
}

async function requestAmbulance(call, destinationHospital) {
  alert(`Requesting ambulance for call ID: ${call?.id}`);
  const destination = clean(destinationHospital);

  if (!destination) {
    setErrMsg("Enter a destination hospital.");
    return false;
  }

  setErrMsg("");
  setSavingKey(`${call.id}:ambulanceRequest`);

 try {
  console.log("REQUEST AMBULANCE START", {
    callId: call.id,
    destination,
    call,
  });

  await updateDoc(doc(db, "dispatchCalls", call.id), {
    callType: "emergency",
    status: "arrivedHQ",
    patientAtHQ: true,

    "hospitalTransport.requested": true,
    "hospitalTransport.acknowledged": false,
    "hospitalTransport.destinationHospital": destination,
    "hospitalTransport.requestedAt": serverTimestamp(),
    "hospitalTransport.ambulanceUnitId": null,

    ambulanceUnitId: null,
    updatedAt: serverTimestamp(),
  });

  return true;
}
 catch (err) {
  console.error("AMBULANCE REQUEST FAILED", err);

  alert(
    JSON.stringify(
      {
        code: err?.code,
        message: err?.message,
      },
      null,
      2
    )
  );

  setErrMsg(err?.message || "Could not request ambulance.");
  return false;
}
}

  async function cancelAmbulance(call) {
    await updateCall(
      call.id,
      {
        "hospitalTransport.requested": false,
        "hospitalTransport.acknowledged": false,
        "hospitalTransport.ambulanceUnitId": null,
        "hospitalTransport.dispatchedAt": null,
        "hospitalTransport.transportingAt": null,
        "hospitalTransport.availableAt": null,
      },
      `${call.id}:ambulanceCancel`
    );
  }

async function requestCourtesy(
  call,
  courtesyDestination,
  courtesyPassengerCount
) {
  const destination = clean(courtesyDestination);
  const count = Number(courtesyPassengerCount || 0);

  if (!destination) {
    setErrMsg("Enter a courtesy destination.");
    return false;
  }

  if (!count || count < 1) {
    setErrMsg("Enter the number of passengers.");
    return false;
  }

  const pickupLocation =
    clean(call.hq?.courtesyPickupLocation) || "Medical HQ";

  // Mark original HQ patient card so tracker shows Courtesy Requested
  await updateCall(
    call.id,
    {
      "hq.courtesyRequested": true,
      "hq.courtesyAcknowledged": false,
      "hq.courtesyQueued": false,
      "hq.courtesyDestination": destination,
      "hq.courtesyPickupLocation": pickupLocation,
      "hq.courtesyPassengerCount": count,
      "hq.courtesyRequestedAt": serverTimestamp(),
    },
    `${call.id}:courtesyRequest`
  );

  // Create standalone transport dispatch call
  await addDoc(collection(db, "dispatchCalls"), {
    callType: "courtesyTransport",
    sourceCallId: call.id,

    patientId: call.patientId || "",
    encounterId: call.encounterId || "",

    status: "requested",

    pickupLocation,
    dropoffLocation: destination,
    passengerCount: count,

    location: null,
    nature: "Courtesy Transport",
    notes: `Transport from ${pickupLocation} to ${destination}`,

    assignedUnitIds: [],
    unitStatuses: {},
    recommendedUnitIds: [],
    suggestedSupervisorIds: [],
    clearedUnitIds: [],

    createdAt: serverTimestamp(),
    requestedAt: serverTimestamp(),
    createdFromHQ: true,

    patientAtHQ: false,
    hq: {
      courtesyRequested: false,
      courtesyAcknowledged: false,
      courtesyQueued: false,
      courtesyDestination: null,
      courtesyPickupLocation: null,
      courtesyPassengerCount: null,
    },
  });

  return true;
}

  async function cancelCourtesy(call) {
    await updateCall(
      call.id,
      {
        "hq.courtesyRequested": false,
        "hq.courtesyAcknowledged": false,
        "hq.courtesyQueued": false,
        "hq.courtesyDestination": null,
        "hq.courtesyPickupLocation": null,
        "hq.courtesyPassengerCount": null,
      },
      `${call.id}:courtesyCancel`
    );
  }

  async function linkCallToChart(call, patientId, encounterId) {
    await updateCall(
      call.id,
      {
        patientId,
        encounterId,
        chartLinkedAt: serverTimestamp(),
      },
      `${call.id}:chartLink`
    );
  }

  async function getBestEncounterId(patient) {
    const encounters = await listEncounters(patient.id);

    const active =
      encounters.find((enc) =>
        ["open", "under care", "awaiting transport"].includes(
          String(enc.status || "").toLowerCase()
        )
      ) || null;

    if (active?.id) return active.id;
    if (encounters[0]?.id) return encounters[0].id;
    return "";
  }

  async function openPatientWorkflow(patient, options = {}) {
    const call = chartModalCall;
    if (!call || !patient?.id) return;

    const { createEncounter: createFreshEncounter = false } = options;

    setChartBusyCallId(call.id);
    setErrMsg("");

    try {
      let encounterId = "";

      if (createFreshEncounter) {
        encounterId = await createEncounter(patient.id, buildEncounterSeed(call));
      } else {
        encounterId = await getBestEncounterId(patient);
        if (!encounterId) {
          encounterId = await createEncounter(patient.id, buildEncounterSeed(call));
        }
      }

      await linkCallToChart(call, patient.id, encounterId);
      setChartModalCall(null);

      navigate(`/field-access/hq/patients/${patient.id}`, {
        state: encounterId ? { selectedEncounterId: encounterId } : undefined,
      });
    } catch (err) {
      console.error("Open existing patient workflow failed:", err);
      setErrMsg(err?.message || "Could not start or open the chart.");
    } finally {
      setChartBusyCallId("");
    }
  }

  async function handleQuickCheckIn(patient, checkIn) {
  const call = chartModalCall;
  if (!call || !patient?.id) return;

  setChartBusyCallId(call.id);
  setErrMsg("");

  try {
    const encounterId = await createEncounter(
      patient.id,
      buildEncounterSeed(call, checkIn)
    );

    await updateCall(
      call.id,
      {
        patientId: patient.id,
        encounterId,
        chartLinkedAt: serverTimestamp(),

        "hq.firstName": checkIn.firstName || patient.firstName || "",
        "hq.lastName": checkIn.lastName || patient.lastName || "",
        "hq.dob": checkIn.dob || patient.dob || "",
        "hq.chiefComplaint": checkIn.complaint || "",
        "hq.incidentRequired": Boolean(checkIn.incidentRequired),
        "hq.incidentStatus": checkIn.incidentRequired ? "pending" : "not-required",
      },
      `${call.id}:quickCheckIn`
    );

    setChartModalCall(null);
    setBoardModalCall(null);
  } catch (err) {
    console.error("Quick check-in failed:", err);
    setErrMsg(err?.message || "Could not check in patient.");
  } finally {
    setChartBusyCallId("");
  }
}

  async function handlePatientCreated(patient) {
    const call = chartModalCall;
    if (!call || !patient?.id) return;

    setChartBusyCallId(call.id);
    setErrMsg("");

    try {
      const encounterId = await createEncounter(patient.id, buildEncounterSeed(call));
      await linkCallToChart(call, patient.id, encounterId);
      setChartModalCall(null);

      navigate(`/field-access/hq/patients/${patient.id}`, {
        state: { selectedEncounterId: encounterId },
      });
    } catch (err) {
      console.error("Create patient workflow failed:", err);
      setErrMsg(err?.message || "Could not create the chart.");
    } finally {
      setChartBusyCallId("");
    }
  }

  function openLinkedChart(call) {
    if (!call?.patientId) return;

    navigate(`/field-access/hq/patients/${call.patientId}`, {
      state: call.encounterId ? { selectedEncounterId: call.encounterId } : undefined,
    });
  }

  function openCourtesyModal(call) {
    setCourtesyModal({
      call,
      destination: clean(call.hq?.courtesyDestination),
      passengers: String(call.hq?.courtesyPassengerCount || 1),
    });
  }

  function openAmbulanceModal(call) {
    setAmbulanceModal({
      call,
      destinationHospital: clean(call.hospitalTransport?.destinationHospital),
    });
  }

  function openDispositionModal(call) {
    setDispositionModal({
      call,
      disposition: clean(call.hq?.dischargeDisposition),
    });
  }

  async function assignBed(call, nextBedRaw) {
    const nextBed = clean(nextBedRaw);

    setErrMsg("");
    setSavingKey(`${call.id}:bed`);

    try {
      if (call.manualTrackerOnly) {
  const patientId = clean(call.patientId);
  const encounterId = clean(call.encounterId);

  await updateDoc(doc(db, "patients", patientId), {
    currentBed: nextBed || "",
    bed: nextBed || "",
    hqBed: nextBed || "",
    assignedBed: nextBed || "",
    updatedAt: serverTimestamp(),
  });

  if (patientId && encounterId) {
    await updateEncounter(patientId, encounterId, {
      bed: nextBed || "",
    });
  }

  return;
}
      await updateDoc(doc(db, "dispatchCalls", call.id), {
        "hq.bedNumber": nextBed || null,
      });

      const patientId = clean(call.patientId);
      const encounterId = clean(call.encounterId);

      if (patientId && encounterId) {
        await updateEncounter(patientId, encounterId, {
          bed: nextBed || "",
        });
      }
    } catch (err) {
      console.error("Bed assignment failed:", err);
      setErrMsg(err?.message || "Could not update bed assignment.");
    } finally {
      setSavingKey("");
    }
  }

  async function moveToWaitingArea(call) {
    await assignBed(call, "");
    setBoardModalCall(null);
  }

  function getDraggedCall() {
    return sortedCalls.find((call) => call.id === draggingCallId) || null;
  }

  function handleCardDragStart(e, call) {
    setDraggingCallId(call.id);
    setDropTarget(null);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", call.id);
  }

  function handleCardDragEnd() {
    setDraggingCallId("");
    setDropTarget(null);
  }

  function canDropIntoBed(call, targetBed) {
    if (!call || !targetBed) return false;
    const currentBed = getDisplayBedForCall(call, patientEncounterMap);
    if (currentBed === targetBed) return true;

    const occupant = (callsByBed[targetBed] || []).find((bedCall) => bedCall.id !== call.id);
    return !occupant;
  }

  function handleBedDragOver(e, bed) {
    const draggedCall = getDraggedCall();
    if (!draggedCall || !canDropIntoBed(draggedCall, bed)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget({ type: "bed", bed });
  }

  function handleUnassignedDragOver(e) {
    if (!getDraggedCall()) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget({ type: "unassigned" });
  }

  function handleDropLeave(bed) {
    setDropTarget((prev) => {
      if (!prev) return prev;
      if (prev.type === "bed" && prev.bed === bed) return null;
      return prev;
    });
  }

  async function handleDropToBed(e, bed) {
    e.preventDefault();
    const draggedCall = getDraggedCall();
    setDropTarget(null);
    if (!draggedCall) return;

    if (!canDropIntoBed(draggedCall, bed)) {
      setErrMsg(`Bed ${bed === "11" ? "VIP" : bed} is already occupied.`);
      return;
    }

    const currentBed = getDisplayBedForCall(draggedCall, patientEncounterMap);
    if (currentBed === bed) return;

    await assignBed(draggedCall, bed);
  }

  async function handleDropToUnassigned(e) {
    e.preventDefault();
    const draggedCall = getDraggedCall();
    setDropTarget(null);
    if (!draggedCall) return;

    const currentBed = getDisplayBedForCall(draggedCall, patientEncounterMap);
    if (!currentBed) return;

    await assignBed(draggedCall, "");
  }

  function renderBoardModal() {
    if (!boardModalCall) return null;

    const call = boardModalCall;
    const chartState = getChartState(call);
    const encounter = chartState.currentEncounter;
    const patientRecord = patientMap[call.patientId] || null;
    const theme = getCardTheme(call);
    const badges = renderChartBadges(call, theme, chartState);

    const hq = call.hq || {};
    const displayName = getPatientDisplayName(patientRecord, hq, call);
    const displayBed = getDisplayBedForCall(call, patientEncounterMap);
    const displayComplaint =
      clean(encounter?.chiefComplaint) ||
      clean(hq.chiefComplaint) ||
      clean(call.nature) ||
      "—";

    return (
      <ActionModal
        title={displayName || "Patient"}
        subtitle={`${displayComplaint} • ${getStatusLabel(call)}`}
        onClose={() => setBoardModalCall(null)}
        footer={
          <button
            type="button"
            style={smallBtn}
            onClick={() => setBoardModalCall(null)}
          >
            Close
          </button>
        }
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {badges}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isPhone
  ? "1fr"
  : "repeat(2, minmax(0, 1fr))",
              gap: 10,
              alignItems: "stretch",
            }}
          >
            {!chartState.isLinked ? (
  isAtHQ(call) ? (
    <button
      type="button"
      onClick={() => {
        setBoardModalCall(null);
        setChartModalCall(call);
      }}
      disabled={chartBusyCallId === call.id}
      style={{
        ...smallBtn,
        borderColor: "#bfdbfe",
        background: "#eff6ff",
        color: "#1d4ed8",
        gridColumn: "1 / -1",
      }}
    >
      {chartBusyCallId === call.id ? "Starting..." : "Start Chart"}
    </button>
  ) : (
    <div
      style={{
        gridColumn: "1 / -1",
        padding: 12,
        borderRadius: 10,
        background: "#f8fafc",
        border: "1px solid #cbd5e1",
        color: "#475569",
        fontWeight: 800,
        textAlign: "center",
      }}
    >
      Chart available when patient arrives at HQ
    </div>
  )
) : (
             <>

    <button
      type="button"
      onClick={() => {
        setBoardModalCall(null);
        openLinkedChart(call);
      }}
      style={{
  ...smallBtn,
  width: "100%",
  minHeight: 48,
  height: 48,
  borderColor: "#bbf7d0",
  background: "#f0fdf4",
  color: "#166534",
  fontWeight: 800,
  gridColumn: "1 / -1",
}}
    >
      Open Chart
    </button>

    <select
      value={displayBed || ""}
      onChange={async (e) => {
        await assignBed(call, e.target.value);
      }}
      style={{
  ...smallInputStyle,
  width: "100%",
  minHeight: 48,
  height: 48,
  paddingTop: 0,
  paddingBottom: 0,
  fontWeight: 800,
  gridColumn: "1 / -1",
}}
    >
      {!displayBed && <option value="">Select Bed</option>}

      {BED_NUMBERS.map((bed) => (
        <option key={bed} value={bed}>
          Bed {bed === "11" ? "VIP" : bed}
        </option>
      ))}
    </select>

    <button
      type="button"
      onClick={() => {
        setBoardModalCall(null);
        openCourtesyModal(call);
      }}
      style={{
        ...smallBtn,
        borderColor: "#fde68a",
        background: "#fef9c3",
        color: "#a16207",
      }}
    >
      Request Courtesy Transport
    </button>

    <button
      type="button"
      onClick={() => {
        setBoardModalCall(null);
        openAmbulanceModal(call);
      }}
      style={{
        ...smallBtn,
        borderColor: "#fca5a5",
        background: "#fee2e2",
        color: "#b91c1c",
      }}
    >
      Request Ambulance
    </button>

    <button
      type="button"
      onClick={async () => {
        await moveToWaitingArea(call);
      }}
      style={{
        ...smallBtn,
        borderColor: "#cbd5e1",
        background: "#f8fafc",
        color: "#334155",
        gridColumn: "1 / -1",
      }}
    >
      Move to Waiting Area
    </button>

    <button
      type="button"
      onClick={() => {
        setBoardModalCall(null);
        openDispositionModal(call);
      }}
      style={{
        ...smallBtn,
        borderColor: "#c7d2fe",
        background: "#eef2ff",
        color: "#4338ca",
        gridColumn: "1 / -1",
      }}
    >
      Disposition
    </button>

    <button
      type="button"
      onClick={async () => {
        await closeEncounterAndRemoveFromBoard(call);
      }}
      style={{
        ...smallBtn,
        borderColor: "#fecaca",
        background: "#fef2f2",
        color: "#b91c1c",
        gridColumn: "1 / -1",
      }}
    >
      Close Encounter / Remove from Board
    </button>
  </>
)}
          </div>
        </div>
      </ActionModal>
    );
  }

  useEffect(() => {
    if (typeof onTotalPatientsChange === "function") {
      onTotalPatientsChange(calls.length);
    }
  }, [calls.length, onTotalPatientsChange]);

  return (
    <div
      className="admin-schedule daily-ops-page"
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "grid",
        gridTemplateRows: "auto minmax(0, 1fr)",
        background: "#f8fafc",
      }}
    >
      <QuickCheckInModal
  isOpen={Boolean(chartModalCall)}
  call={chartModalCall}
  patients={patients}
  onClose={() => {
    if (!chartBusyCallId) setChartModalCall(null);
  }}
  onCheckInPatient={handleQuickCheckIn}
/>

      {renderBoardModal()}

      {courtesyModal && (
        <ActionModal
          title="Courtesy Transport"
          subtitle="Enter destination and passenger count."
          onClose={() => setCourtesyModal(null)}
          footer={
            <>
              {courtesyModal.call?.hq?.courtesyRequested ? (
                <button
                  type="button"
                  style={{
                    ...smallBtn,
                    borderColor: "#fde68a",
                    background: "#fde68a",
                    color: "#92400e",
                  }}
                  onClick={async () => {
                    await cancelCourtesy(courtesyModal.call);
                    setCourtesyModal(null);
                  }}
                >
                  Cancel Courtesy
                </button>
              ) : null}

              <button
                type="button"
                style={smallBtn}
                onClick={() => setCourtesyModal(null)}
              >
                Close
              </button>

              <button
                type="button"
                style={{
                  ...smallBtn,
                  borderColor: "#fde68a",
                  background: "#fef9c3",
                  color: "#a16207",
                }}
                onClick={async () => {
                  const ok = await requestCourtesy(
                    courtesyModal.call,
                    courtesyModal.destination,
                    courtesyModal.passengers
                  );
                  if (ok) setCourtesyModal(null);
                }}
              >
                Save Courtesy
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                Destination
              </span>
              <input
                style={smallInputStyle}
                value={courtesyModal.destination}
                onChange={(e) =>
                  setCourtesyModal((prev) => ({ ...prev, destination: e.target.value }))
                }
                placeholder="Courtesy destination"
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                Passenger Count
              </span>
              <input
                type="number"
                min="1"
                style={smallInputStyle}
                value={courtesyModal.passengers}
                onChange={(e) =>
                  setCourtesyModal((prev) => ({ ...prev, passengers: e.target.value }))
                }
                placeholder="Passengers"
              />
            </label>
          </div>
        </ActionModal>
      )}

      {ambulanceModal && (
        <ActionModal
          title="Request Ambulance"
          subtitle="Enter destination hospital."
          onClose={() => setAmbulanceModal(null)}
          footer={
            <>
              {ambulanceModal.call?.hospitalTransport?.requested ? (
                <button
                  type="button"
                  style={{
                    ...smallBtn,
                    borderColor: "#fca5a5",
                    background: "#fecaca",
                    color: "#b91c1c",
                  }}
                  onClick={async () => {
                    await cancelAmbulance(ambulanceModal.call);
                    setAmbulanceModal(null);
                  }}
                >
                  Cancel Request
                </button>
              ) : null}

              <button type="button" style={smallBtn} onClick={() => setAmbulanceModal(null)}>
                Close
              </button>

              <button
                type="button"
                style={{
                  ...smallBtn,
                  borderColor: "#fca5a5",
                  background: "#fee2e2",
                  color: "#b91c1c",
                }}
               onClick={async () => {
  const ok = await requestAmbulance(
    ambulanceModal.call,
    ambulanceModal.destinationHospital
  );

  if (ok) setAmbulanceModal(null);
}}
              >
                Save Request
              </button>
            </>
          }
        >
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
              Destination Hospital
            </span>
            <input
              style={smallInputStyle}
              value={ambulanceModal.destinationHospital}
              onChange={(e) =>
                setAmbulanceModal((prev) => ({
                  ...prev,
                  destinationHospital: e.target.value,
                }))
              }
              placeholder="Destination hospital"
            />
          </label>
        </ActionModal>
      )}

      {inventoryModalOpen && (
  <ActionModal
    title="HQ Inventories"
    subtitle="Assigned inventories for Medical HQ."
    onClose={() => setInventoryModalOpen(false)}
    footer={
      <button
        type="button"
        style={smallBtn}
        onClick={() => setInventoryModalOpen(false)}
      >
        Close
      </button>
    }
  >
    {inventoryLoading ? (
      <div>Loading inventories...</div>
    ) : hqInventories.length === 0 ? (
      <div style={{ color: "#64748b" }}>No HQ inventories assigned.</div>
    ) : (
      <div style={{ display: "grid", gap: 10 }}>
        {hqInventories.map(({ assignment, template }) => (
          <div
            key={assignment.id}
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 12,
              padding: 12,
              background: "#f8fafc",
            }}
          >
            <strong>{template.name}</strong>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {template.category || "Inventory"} ·{" "}
              {(template.locations || []).length} section(s)
            </div>
          </div>
        ))}
      </div>
    )}
  </ActionModal>
)}

      {dispositionModal && (
        <ActionModal
          title="Disposition"
          subtitle="Save a tracker disposition and mirror it into the chart."
          onClose={() => setDispositionModal(null)}
          footer={
            <>
              <button type="button" style={smallBtn} onClick={() => setDispositionModal(null)}>
                Close
              </button>
              <button
                type="button"
                style={{
                  ...smallBtn,
                  borderColor: "#bfdbfe",
                  background: "#eff6ff",
                  color: "#1d4ed8",
                }}
                onClick={async () => {
                  if (!clean(dispositionModal.disposition)) {
                    setErrMsg("Choose a disposition.");
                    return;
                  }
                  await applyDisposition(
                    dispositionModal.call,
                    dispositionModal.disposition
                  );
                }}
              >
                Save Disposition
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 8 }}>
            {DISPOSITION_OPTIONS.map((option) => {
              const selected = clean(dispositionModal.disposition) === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() =>
                    setDispositionModal((prev) => ({ ...prev, disposition: option }))
                  }
                  style={{
                    ...smallBtn,
                    minHeight: 42,
                    justifyContent: "flex-start",
                    borderColor: selected ? "#bfdbfe" : "#cbd5e1",
                    background: selected ? "#eff6ff" : "#ffffff",
                    color: selected ? "#1d4ed8" : "#0f172a",
                  }}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </ActionModal>
      )}

      {!!errMsg && (
        <div
          style={{
            padding: 12,
            color: "#991b1b",
            background: "#fff1f2",
            borderRadius: 12,
            margin: "6px 8px 8px",
            border: "1px solid #fecdd3",
          }}
        >
          {errMsg}
        </div>
      )}

      <div
        style={{
          minHeight: 0,
          overflowY: "auto",
          padding: "8px 8px 8px",
          display: "grid",
          gap: 8,
          alignContent: "start",
        }}
      >
        <div
          style={{
            display: "grid",
           gridTemplateColumns: isTablet
  ? "1fr"
  : "230px minmax(0, 1fr)",
            gap: 10,
            alignItems: "start",
          }}
        >
          <div
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 16,
              background: "#ffffff",
              padding: 6,
              display: "grid",
              gap: 4,
              alignContent: "start",
              position: isTablet ? "relative" : "sticky",
              top: 0,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
              Waiting Room
            </div>

            <div
              onDragOver={handleUnassignedDragOver}
              onDragLeave={() => {
                setDropTarget((prev) => (prev?.type === "unassigned" ? null : prev));
              }}
              onDrop={handleDropToUnassigned}
              style={{
                minHeight: 78,
                borderRadius: 14,
                border: `2px dashed ${dropTarget?.type === "unassigned" ? "#22c55e" : "#e2e8f0"}`,
                background: dropTarget?.type === "unassigned" ? "#dcfce7" : "#f8fafc",
                padding: 6,
                display: "grid",
                gap: 6,
                alignItems: "start",
                transition: "background 120ms ease, border-color 120ms ease",
              }}
            >
              {!unassignedCalls.length ? (
                <div style={{ color: dropTarget?.type === "unassigned" ? "#166534" : "#64748b", fontSize: 13, fontWeight: dropTarget?.type === "unassigned" ? 800 : 500 }}>
                  {dropTarget?.type === "unassigned"
                    ? "Drop patient here to remove from bed"
                    : "No waiting pts."}
                </div>
              ) : (
                unassignedCalls.map((call) => {
                  const chartState = getChartState(call);
                  const encounter = chartState.currentEncounter;
                  const patientRecord = patientMap[call.patientId] || null;
                  const badges = renderChartBadges(
                    call,
                    getCardTheme(call),
                    chartState
                  );

                  return (
                    <TrackerPatientCard
                      key={call.id}
                      call={call}
                      patientRecord={patientRecord}
                      encounter={encounter}
                      chartState={chartState}
                      chartBusyCallId={chartBusyCallId}
                      setChartModalCall={setChartModalCall}
                      openLinkedChart={openLinkedChart}
                      openCourtesyModal={openCourtesyModal}
                      openAmbulanceModal={openAmbulanceModal}
                      assignBed={assignBed}
                      patientEncounterMap={patientEncounterMap}
                      badges={badges}
                      compact
                      onOpenBoardModal={setBoardModalCall}
                      draggable
                      onDragStart={handleCardDragStart}
                      onDragEnd={handleCardDragEnd}
                    />
                  );
                })
              )}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 16,
              background: "#ffffff",
              padding: 8,
              display: "grid",
              gap: 6,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
              HQ Bed Layout
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isTablet
  ? "1fr"
  : "minmax(0, 1fr) 108px",
                gap: 6,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  border: "2px solid #94a3b8",
                  borderRadius: 20,
                  minHeight: 0,
                  padding: 6,
                  background: "#f8fafc",
                  display: "grid",
                  gridTemplateColumns: isPhone
  ? "1fr"
  : "repeat(2, minmax(0, 1fr))",
                  gap: 6,
                  alignContent: "start",
                }}
              >
                <BedDropTile
                  label="Bed 1"
                  bed="1"
                  bedCalls={callsByBed["1"] || []}
                  chartBusyCallId={chartBusyCallId}
                  setChartModalCall={setChartModalCall}
                  openLinkedChart={openLinkedChart}
                  openCourtesyModal={openCourtesyModal}
                  openAmbulanceModal={openAmbulanceModal}
                  assignBed={assignBed}
                  patientEncounterMap={patientEncounterMap}
                  onOpenBoardModal={setBoardModalCall}
                  onDragStart={handleCardDragStart}
                  onDragEnd={handleCardDragEnd}
                  onDragOver={handleBedDragOver}
                  onDragLeave={handleDropLeave}
                  onDrop={handleDropToBed}
                  isDropActive={dropTarget?.type === "bed" && dropTarget?.bed === "1"}
                  getChartState={getChartState}
                  patientMap={patientMap}
                  renderChartBadges={renderChartBadges}
                />

                <div
                  style={{
                    height: BED_TILE_HEIGHT,
                    border: "2px dashed #94a3b8",
                    borderRadius: 14,
                    background: "#fff7ed",
                    padding: 4,
                    display: "grid",
                    alignContent: "center",
                    justifyItems: "center",
                    textAlign: "center",
                    color: "#9a3412",
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: 13 }}>ENTRANCE</div>
                  <div style={{ fontSize: 10, fontWeight: 700 }}>DOOR</div>
                </div>

                <BedDropTile
                  label="Bed 2"
                  bed="2"
                  bedCalls={callsByBed["2"] || []}
                  chartBusyCallId={chartBusyCallId}
                  setChartModalCall={setChartModalCall}
                  openLinkedChart={openLinkedChart}
                  openCourtesyModal={openCourtesyModal}
                  openAmbulanceModal={openAmbulanceModal}
                  assignBed={assignBed}
                  patientEncounterMap={patientEncounterMap}
                  onOpenBoardModal={setBoardModalCall}
                  onDragStart={handleCardDragStart}
                  onDragEnd={handleCardDragEnd}
                  onDragOver={handleBedDragOver}
                  onDragLeave={handleDropLeave}
                  onDrop={handleDropToBed}
                  isDropActive={dropTarget?.type === "bed" && dropTarget?.bed === "2"}
                  getChartState={getChartState}
                  patientMap={patientMap}
                  renderChartBadges={renderChartBadges}
                />

                <div
                  style={{
                    height: BED_TILE_HEIGHT,
                    border: "2px dashed #7dd3fc",
                    borderRadius: 14,
                    background: "#ecfeff",
                    padding: 4,
                    display: "grid",
                    alignContent: "center",
                    justifyItems: "center",
                    textAlign: "center",
                    color: "#155e75",
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: 13 }}>TRIAGE</div>
                  <div style={{ fontSize: 10, fontWeight: 700 }}>
                    Intake / first contact
                  </div>
                </div>

                {["3", "7", "4", "8", "5", "9", "6", "10"].map((bed) => (
                  <BedDropTile
                    key={bed}
                    label={`Bed ${bed}`}
                    bed={bed}
                    bedCalls={callsByBed[bed] || []}
                    chartBusyCallId={chartBusyCallId}
                    setChartModalCall={setChartModalCall}
                    openLinkedChart={openLinkedChart}
                    openCourtesyModal={openCourtesyModal}
                    openAmbulanceModal={openAmbulanceModal}
                    assignBed={assignBed}
                    patientEncounterMap={patientEncounterMap}
                    onOpenBoardModal={setBoardModalCall}
                    onDragStart={handleCardDragStart}
                    onDragEnd={handleCardDragEnd}
                    onDragOver={handleBedDragOver}
                    onDragLeave={handleDropLeave}
                    onDrop={handleDropToBed}
                    isDropActive={dropTarget?.type === "bed" && dropTarget?.bed === bed}
                    getChartState={getChartState}
                    patientMap={patientMap}
                    renderChartBadges={renderChartBadges}
                  />
                ))}
              </div>

              <BedDropTile
                label="VIP/Bed 11"
                bed="11"
                bedCalls={callsByBed["11"] || []}
                isVip
                chartBusyCallId={chartBusyCallId}
                setChartModalCall={setChartModalCall}
                openLinkedChart={openLinkedChart}
                openCourtesyModal={openCourtesyModal}
                openAmbulanceModal={openAmbulanceModal}
                assignBed={assignBed}
                patientEncounterMap={patientEncounterMap}
                onOpenBoardModal={setBoardModalCall}
                onDragStart={handleCardDragStart}
                onDragEnd={handleCardDragEnd}
                onDragOver={handleBedDragOver}
                onDragLeave={handleDropLeave}
                onDrop={handleDropToBed}
                isDropActive={dropTarget?.type === "bed" && dropTarget?.bed === "11"}
                getChartState={getChartState}
                patientMap={patientMap}
                renderChartBadges={renderChartBadges}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="print-header">Travelers Championship Medical — HQ Patient Tracking</div>
      <div className="print-footer">Generated {new Date().toLocaleString()}</div>
    </div>
    );
}

function getLinkedEncounter(call, patientEncounterMap) {
  const patientId = clean(call.patientId);
  const encounterId = clean(call.encounterId);
  const patientEncounters = patientEncounterMap[patientId] || [];

  if (!patientId || !encounterId) return null;

  return patientEncounters.find((enc) => enc.id === encounterId) || null;
}
